import { useState } from 'react';
import {
  VStack, HStack, Text, Heading, Button, Box, Radio, RadioGroup,
} from '@chakra-ui/react';

// Plan ids/prices MUST match the backend SubscriptionPlan enum + PlanConfiguration.
// (FR-060 single source: the further step is to fetch these from GET /api/subscription/plans;
// there is no `free` tier — all plans are paid per FR-005.)
const PLANS = [
  {
    id: 'start',
    name: 'Start',
    price: '$1.99',
    period: '/month',
    features: ['1 location', '50 audio uploads', 'Public-use license', 'Air-raid alert handling'],
    highlight: false,
  },
  {
    id: 'business',
    name: 'Business',
    price: '$9.99',
    period: '/month',
    features: ['Up to 5 locations', '500 audio uploads', 'AI ad generation', 'Custom playlists', 'Analytics'],
    highlight: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$19.99',
    period: '/month',
    features: ['Up to 25 locations', '5000 audio uploads', 'Spotify import', 'API access', 'All Business features'],
    highlight: false,
  },
];

export interface SubscriptionStepProps {
  onSubmit: (planId: string) => void;
  initialPlan?: string;
  isLoading?: boolean;
  error?: string | null;
}

export function SubscriptionStep({ onSubmit, initialPlan, isLoading, error }: SubscriptionStepProps) {
  const [selectedPlan, setSelectedPlan] = useState(initialPlan ?? 'start');

  return (
    <VStack
      flex={1}
      p={6}
      gap={6}
      bg="gray.50"
      overflowY="auto"
      data-testid="wizard-subscription-step"
    >
      <VStack align="center" gap={2}>
        <Heading as="h2" size="xl" fontWeight="bold" color="gray.800">
          Choose your plan
        </Heading>
        <Text fontSize="md" color="gray.500" textAlign="center">
          Start with a free trial — you can upgrade anytime
        </Text>
      </VStack>

      <RadioGroup value={selectedPlan} onChange={setSelectedPlan}>
        <VStack gap={4} w="100%" maxW="800px" alignSelf="center">
          <HStack gap={4} wrap="wrap" justify="center" w="100%">
            {PLANS.map((plan) => (
              <Box
                key={plan.id}
                p={5}
                bg="white"
                borderRadius="md"
                borderWidth="2px"
                borderColor={selectedPlan === plan.id ? 'brand.500' : 'gray.200'}
                minW="220px"
                flex={1}
                cursor="pointer"
                onClick={() => setSelectedPlan(plan.id)}
                transition="all 0.15s"
                position="relative"
                _hover={{ borderColor: 'brand.300' }}
                data-testid={`wizard-plan-${plan.id}`}
              >
                {plan.highlight && (
                  <Box
                    position="absolute"
                    top="-12px"
                    left="50%"
                    transform="translateX(-50%)"
                    bg="brand.500"
                    color="white"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    Popular
                  </Box>
                )}
                <VStack gap={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="700" fontSize="lg" color="gray.800">{plan.name}</Text>
                    <Radio value={plan.id} colorScheme="brand" />
                  </HStack>
                  <HStack align="baseline" gap={1}>
                    <Text fontWeight="800" fontSize="3xl" color="gray.800">{plan.price}</Text>
                    <Text fontSize="sm" color="gray.500">{plan.period}</Text>
                  </HStack>
                  <VStack gap={2} align="stretch" mt={2}>
                    {plan.features.map((feature) => (
                      <HStack key={feature} gap={2} align="center">
                        <Text color="green.500" fontSize="sm">✓</Text>
                        <Text fontSize="sm" color="gray.600">{feature}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </Box>
            ))}
          </HStack>
        </VStack>
      </RadioGroup>

      {error && (
        <Text color="red.500" fontSize="sm" textAlign="center">
          {error}
        </Text>
      )}

      <Button
        size="lg"
        bg="brand.500"
        color="white"
        fontWeight="bold"
        w="100%"
        maxW="400px"
        alignSelf="center"
        mt={4}
        onClick={() => onSubmit(selectedPlan)}
        isDisabled={isLoading}
        data-testid="wizard-subscription-submit"
        _hover={{ bg: 'brand.600' }}
      >
        {isLoading ? 'Processing...' : 'Start Free Trial'}
      </Button>
    </VStack>
  );
}

export default SubscriptionStep;
